import React, { useEffect, useMemo, useState } from 'react';
import './account.css';
import pb from '../lib/pocketbase';
import { usePDFGenerator } from '../hooks/usePDFGenerator';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';

export default function Account() {
  const tabs = ['Accueil', 'Profil', 'Véhicules', 'Devis', 'Factures', 'Rendez-vous', 'Historique', 'Contact'];
  const [tab, setTab] = useState('Accueil');
  const user = pb?.authStore?.model || null;
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [originalCustomer, setOriginalCustomer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [appointmentSlots, setAppointmentSlots] = useState({}); // { [appointmentId]: slots[] }
  const [appointmentSavingId, setAppointmentSavingId] = useState(null);
  const [appointmentsView, setAppointmentsView] = useState('list'); // 'list' | 'calendar'
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const { generateQuotePDF, generateInvoicePDF, isGenerating } = usePDFGenerator();
  const [qSearch, setQSearch] = useState('');
  const [iSearch, setISearch] = useState('');
  const [quoteSavingId, setQuoteSavingId] = useState(null);
  const [archivesModalOpen, setArchivesModalOpen] = useState(false);
  const [archivesModalLoading, setArchivesModalLoading] = useState(false);
  const [archivesModalTitle, setArchivesModalTitle] = useState('');
  const [archivesModalItems, setArchivesModalItems] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  // Connexion rapide (état non connecté)
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    const datePart = d.toLocaleDateString();
    const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return '';
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value));
    } catch {
      return `${Number(value).toFixed(2)} €`;
    }
  };

  const getApptStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return '#3b82f6';
      case 'in_progress':
        return '#f59e0b';
      case 'completed':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return '';
    const ms = new Date(endIso) - new Date(startIso);
    if (ms <= 0) return '';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  };

  const pad = (n) => String(n).padStart(2, '0');
  const toICSDateUTC = (d) => {
    const dt = new Date(d);
    return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth()+1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}${pad(dt.getUTCSeconds())}Z`;
  };

  const generateICSForAppointment = (appt) => {
    const uid = `appt-${appt.id}@christophedepanne`;
    const title = appt.title || 'Rendez-vous';
    const start = toICSDateUTC(appt.start_date || new Date());
    const end = toICSDateUTC(appt.end_date || appt.start_date || new Date());
    const descriptionParts = [];
    if (appt.expand?.vehicle_id) {
      const v = appt.expand.vehicle_id;
      descriptionParts.push(`Véhicule: ${v.brand || ''} ${v.model || ''}${v.license_plate ? ` (${v.license_plate})` : ''}`.trim());
    }
    if (appt.notes) descriptionParts.push(`Notes: ${appt.notes}`);
    const description = descriptionParts.join('\n');
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Christophe Depanne//Appointments//FR',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${toICSDateUTC(new Date())}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${title.replace(/\n/g, ' ')}`,
      description ? `DESCRIPTION:${description.replace(/[\n\r]/g, '\\n')}` : 'DESCRIPTION:',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  };

  const handleAddToCalendar = (appt) => {
    try {
      const ics = generateICSForAppointment(appt);
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const number = appt?.id?.slice?.(0,6) || '';
      a.download = `RendezVous_${number || ''}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erreur génération ICS:', e);
      alert("Impossible de générer l'événement calendrier.");
    }
  };

  // Notifications pour l'onglet Historique
  const [historyUnread, setHistoryUnread] = useState(0);
  const [historyLastSeen, setHistoryLastSeen] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem('account_history_last_seen') || null;
    } catch {
      return null;
    }
  });
  const [showNotifMenu, setShowNotifMenu] = useState(false);

  const isLoggedIn = !!user?.id;

  const pendingQuotesCount = useMemo(
    () => quotes.filter(q => q.status === 'sent').length,
    [quotes]
  );

  const unpaidInvoicesCount = useMemo(
    () => invoices.filter(inv => inv.status && inv.status !== 'paid' && inv.status !== 'draft').length,
    [invoices]
  );

  const proposedSlotsCount = useMemo(
    () => Object.values(appointmentSlots || {}).reduce((acc, arr) => acc + ((arr && arr.length) || 0), 0),
    [appointmentSlots]
  );

  const lastActivity = useMemo(
    () => (history && history.length > 0 ? history[0] : null),
    [history]
  );

  const lastActivityDisplayText = useMemo(() => {
    if (!lastActivity) return null;

    const log = lastActivity;
    const coll = log.collection || '';
    const typeLabel = coll === 'quotes' ? 'Devis' : coll === 'invoices' ? 'Facture' : (coll || 'Autre');
    const isClientAction = log.source === 'account' || log.performed_by_role === 'client';

    let actionLabel = '';
    switch (log.action) {
      case 'create':
        actionLabel = 'Création';
        break;
      case 'update':
        actionLabel = 'Mise à jour';
        break;
      case 'delete':
        actionLabel = 'Suppression';
        break;
      case 'status_change':
        actionLabel = 'Statut modifié';
        break;
      case 'share':
        actionLabel = 'Partage';
        break;
      default:
        actionLabel = log.action || '';
    }

    let displayText = '';
    if (coll === 'quotes' && log.action === 'status_change') {
      const toStatus = log.context?.to_status || log.context?.toStatus || null;
      if (isClientAction) {
        if (toStatus === 'accepted') displayText = 'Vous avez accepté ce devis.';
        else if (toStatus === 'rejected') displayText = 'Vous avez refusé ce devis.';
        else if (toStatus === 'sent') displayText = 'Vous avez annulé votre réponse à ce devis.';
        else displayText = 'Vous avez modifié le statut de ce devis.';
      } else {
        if (toStatus === 'accepted') displayText = 'Votre devis a été marqué comme accepté.';
        else if (toStatus === 'rejected') displayText = 'Votre devis a été marqué comme refusé.';
        else if (toStatus === 'sent') displayText = 'Le devis a été modifié et est de nouveau en attente de votre réponse.';
        else displayText = 'Devis en attente de réponse.';
      }
    } else if (coll === 'invoices' && log.action === 'create') {
      displayText = isClientAction
        ? 'Vous avez créé une nouvelle facture.'
        : 'Vous avez reçu une nouvelle facture.';
    } else if (coll === 'quotes' && log.action === 'create') {
      displayText = isClientAction
        ? 'Vous avez créé un nouveau devis.'
        : 'Un nouveau devis a été créé pour vous.';
    } else if (log.action === 'share') {
      if (coll === 'quotes') displayText = isClientAction
        ? 'Vous avez partagé votre devis.'
        : 'Votre devis a été partagé.';
      else if (coll === 'invoices') displayText = isClientAction
        ? 'Vous avez partagé votre facture.'
        : 'Votre facture a été partagée.';
    }

    if (!displayText) {
      displayText = log.message || `Action ${actionLabel || ''} sur ${typeLabel}`;
    }
    if (displayText) {
      displayText = displayText.replace(" depuis l'admin", '').replace(' depuis l’admin', '');
    }
    return displayText;
  }, [lastActivity]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (!isLoggedIn) return;
      // Récupérer le client lié à l'utilisateur
      const c = await pb.collection('customers').getFirstListItem(`created_by = "${user.id}"`).catch(() => null);
      setCustomer(c);
      // Véhicules
      if (c?.id) {
        const v = await pb.collection('vehicles').getFullList({ filter: `customer_id = "${c.id}"`, sort: '-created' });
        setVehicles(v || []);
      } else {
        setVehicles([]);
      }
      // Devis et factures (affichage basique)
      if (c?.id) {
        const qs = await pb.collection('quotes').getFullList({ filter: `customer_id = "${c.id}"`, sort: '-created' }).catch(() => []);
        const fs = await pb.collection('invoices').getFullList({ filter: `customer_id = "${c.id}"`, sort: '-created' }).catch(() => []);
        // Ne pas montrer les brouillons dans l'espace client
        const quotesForClient = (qs || []).filter(q => q.status !== 'draft');
        const invoicesForClient = (fs || []).filter(inv => inv.status !== 'draft');
        setQuotes(quotesForClient);
        setInvoices(invoicesForClient);

        // Rendez-vous + créneaux proposés
        const appts = await pb.collection('appointments').getFullList({ filter: `customer_id = "${c.id}"`, sort: '-start_date', expand: 'vehicle_id,quote_id' }).catch(() => []);
        setAppointments(appts || []);
        try {
          const slots = await pb.collection('appointment_slots').getFullList({ filter: `appointment_id != '' && status = 'proposed'` }).catch(() => []);
          const map = {};
          for (const s of (slots || [])) {
            if (!map[s.appointment_id]) map[s.appointment_id] = [];
            map[s.appointment_id].push(s);
          }
          setAppointmentSlots(map);
        } catch {
          setAppointmentSlots({});
        }

        // Préparer les IDs pour filtrer les logs liés à ces documents
        const quoteIds = new Set(quotesForClient.map(q => q.id));
        const invoiceIds = new Set(invoicesForClient.map(inv => inv.id));

        // Historique: inclure les actions du client ET les actions admin/system sur ses devis/factures
        if (user?.id) {
          const logs = await pb.collection('activity_logs').getFullList({
            sort: '-created',
            filter: 'collection = "quotes" || collection = "invoices" || performed_by_user != ""'
          }).catch(() => []);

          const visibleLogs = (logs || []).filter(log => {
            // Ne pas afficher les logs techniques de génération PDF côté client
            if (log.action === 'pdf_generated') return false;
            // Logs faits par ce client
            if (log.performed_by_user === user.id) return true;
            // Logs liés aux devis du client
            if (log.collection === 'quotes' && quoteIds.has(log.record_id)) return true;
            // Logs liés aux factures du client
            if (log.collection === 'invoices' && invoiceIds.has(log.record_id)) return true;
            return false;
          });
          setHistory(visibleLogs);
        } else {
          setHistory([]);
        }
      } else {
        setQuotes([]);
        setInvoices([]);
        setHistory([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [isLoggedIn]);
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await pb.collection('settings').getList(1, 1, { sort: '-updated' });
        setSiteSettings(res?.items?.[0] || null);
      } catch {}
    };
    fetchSettings();
  }, []);

  // Mettre à jour le compteur de notifications non lues quand l'historique change
  useEffect(() => {
    if (tab === 'Historique') return;
    if (!history || history.length === 0) {
      setHistoryUnread(0);
      return;
    }
    const lastSeenDate = historyLastSeen ? new Date(historyLastSeen) : null;
    const unread = history.filter(log => {
      if (!lastSeenDate) return true;
      return new Date(log.created) > lastSeenDate;
    }).length;
    setHistoryUnread(unread);
  }, [history, tab, historyLastSeen]);

  // Quand l'utilisateur ouvre l'onglet Historique, marquer les notifications comme lues
  useEffect(() => {
    if (tab !== 'Historique') return;
    const now = new Date().toISOString();
    setHistoryLastSeen(now);
    setHistoryUnread(0);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('account_history_last_seen', now);
      }
    } catch {}
  }, [tab]);

  // Fermer le menu de notifications quand on clique en dehors ou quand on change d'onglet
  useEffect(() => {
    if (!showNotifMenu) return;

    const handleClick = (e) => {
      const target = e.target;
      if (!target.closest || !target.closest('.notif-icon-wrap')) {
        setShowNotifMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showNotifMenu]);

  // Abonnements temps réel pour les données du client (devis, factures, rdv, slots, historique)
  useEffect(() => {
    if (!isLoggedIn || !customer?.id) return;

    const customerId = customer.id;

    const unsubQuotes = pb.collection('quotes').subscribe('*', async (e) => {
      try {
        if (e.record?.customer_id !== customerId) return;
        await loadData();
      } catch (err) {
        console.error('Erreur reload quotes realtime (compte):', err);
      }
    });

    const unsubInvoices = pb.collection('invoices').subscribe('*', async (e) => {
      try {
        if (e.record?.customer_id !== customerId) return;
        await loadData();
      } catch (err) {
        console.error('Erreur reload invoices realtime (compte):', err);
      }
    });

    const unsubAppointments = pb.collection('appointments').subscribe('*', async (e) => {
      try {
        if (e.record?.customer_id !== customerId) return;
        await loadData();
      } catch (err) {
        console.error('Erreur reload appointments realtime (compte):', err);
      }
    });

    const unsubSlots = pb.collection('appointment_slots').subscribe('*', async () => {
      try {
        // On recharge sans filtrer finement ici (simplicité)
        await loadData();
      } catch (err) {
        console.error('Erreur reload appointment_slots realtime (compte):', err);
      }
    });

    const unsubLogs = pb.collection('activity_logs').subscribe('*', async () => {
      try {
        await loadData();
      } catch (err) {
        console.error('Erreur reload activity_logs realtime (compte):', err);
      }
    });

    return () => {
      try { unsubQuotes?.(); } catch {}
      try { unsubInvoices?.(); } catch {}
      try { unsubAppointments?.(); } catch {}
      try { unsubSlots?.(); } catch {}
      try { unsubLogs?.(); } catch {}
    };
  }, [isLoggedIn, customer?.id]);

  // Actions rendez-vous côté client
  const acceptAppointmentSlot = async (appointment, slot) => {
    if (!appointment?.id || !slot?.id) return;
    setAppointmentSavingId(appointment.id);
    try {
      // 1) Fixer le RDV sur le créneau choisi + marquer comme accepté côté client
      await pb.collection('appointments').update(appointment.id, {
        start_date: slot.start,
        end_date: slot.end,
        rdv_status: 'accepted',
        accepted_at: new Date().toISOString(),
        chosen_slot_id: slot.id,
      });
      // 2) Mettre à jour les slots (schéma: proposed | selected | expired | canceled)
      await pb.collection('appointment_slots').update(slot.id, { status: 'selected' });
      const siblings = (appointmentSlots[appointment.id] || []).filter(s => s.id !== slot.id);
      for (const o of siblings) {
        try { await pb.collection('appointment_slots').update(o.id, { status: 'expired' }); } catch {}
      }
      // 3) Log d'événement 'accepted' côté client
      try {
        await pb.collection('appointment_events').create({
          appointment_id: appointment.id,
          type: 'accepted',
          payload: { slot_id: slot.id, source: 'account' },
        });
      } catch {}
      await loadData();
      alert('Votre créneau a été confirmé.');
    } catch (e) {
      console.error('Erreur acceptation créneau (compte):', e);
      alert('Impossible de confirmer ce créneau.');
    } finally {
      setAppointmentSavingId(null);
    }
  };

  const refuseAllAppointmentSlots = async (appointment) => {
    if (!appointment?.id) return;
    if (!window.confirm('Refuser tous les créneaux proposés ?')) return;
    setAppointmentSavingId(appointment.id);
    try {
      const siblings = appointmentSlots[appointment.id] || [];
      for (const o of siblings) {
        try { await pb.collection('appointment_slots').update(o.id, { status: 'void', is_available: false }); } catch {}
      }
      try {
        await pb.collection('appointment_events').create({
          appointment_id: appointment.id,
          type: 'refused',
          payload: { source: 'account' },
        });
      } catch {}
      await loadData();
      alert('Vous avez refusé ces créneaux. Nous vous recontacterons.');
    } catch (e) {
      console.error('Erreur refus créneaux (compte):', e);
      alert('Impossible de refuser les créneaux.');
    } finally {
      setAppointmentSavingId(null);
    }
  };

  const handleProfileSave = async () => {
    if (!customer) return;
    try {
      setSaving(true);
      await pb.collection('customers').update(customer.id, {
        name: customer.name || 'Client',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        company: customer.company || ''
      });
      setOriginalCustomer(customer);
    } finally {
      setSaving(false);
    }
  };

  const [editingProfile, setEditingProfile] = useState(false);
  const startEditProfile = () => {
    if (customer) {
      setOriginalCustomer(customer);
      setEditingProfile(true);
    }
  };
  const cancelEditProfile = () => {
    if (originalCustomer) {
      setCustomer(originalCustomer);
    }
    setEditingProfile(false);
  };

  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [originalVehicle, setOriginalVehicle] = useState(null);
  const [savingVehicleId, setSavingVehicleId] = useState(null);

  const handleAddVehicle = () => {
    if (!customer?.id) return;
    const tempId = `temp-${Date.now()}`;
    const draft = {
      id: null,
      _tempId: tempId,
      customer_id: customer.id,
      brand: '',
      model: '',
      year: '',
      license_plate: '',
      mileage: ''
    };
    setVehicles(v => [draft, ...v]);
    setOriginalVehicle(draft);
    setEditingVehicleId(tempId);
  };

  const handleUpdateVehicle = (i, patch) => {
    const v = vehicles[i];
    if (!v) return;
    const updated = { ...v, ...patch };
    setVehicles(list => list.map((x, idx) => (idx === i ? updated : x)));
  };

  const startEditVehicle = (i) => {
    const v = vehicles[i];
    if (!v) return;
    setOriginalVehicle(v);
    setEditingVehicleId(v.id || `idx-${i}`);
  };

  const cancelEditVehicle = (i) => {
    if (originalVehicle) {
      setVehicles(list => list.map((x, idx) => (idx === i ? originalVehicle : x)));
    }
    setEditingVehicleId(null);
    setOriginalVehicle(null);
  };

  const saveVehicle = async (i) => {
    const v = vehicles[i];
    if (!v) return;

    const payload = {
      customer_id: customer?.id,
      brand: v.brand || '',
      model: v.model || '',
      year: v.year ? Number(v.year) : undefined,
      license_plate: v.license_plate || '',
      mileage: v.mileage ? Number(String(v.mileage).replace(/\s+/g,'').replace(/[^\d]/g,'')) : undefined
    };

    const keyId = v.id || v._tempId;
    setSavingVehicleId(keyId);
    try {
      if (!v.id) {
        // Création
        const rec = await pb.collection('vehicles').create(payload);
        setVehicles(list => list.map((x, idx) => (
          idx === i ? { ...x, id: rec.id, _tempId: undefined } : x
        )));
      } else {
        // Mise à jour
        await pb.collection('vehicles').update(v.id, payload);
      }
      setEditingVehicleId(null);
      setOriginalVehicle(null);
    } catch (e) {
      console.error('Erreur sauvegarde véhicule (compte):', e);
      alert('Erreur lors de la sauvegarde du véhicule');
    } finally {
      setSavingVehicleId(null);
    }
  };

  const handleDeleteVehicle = async (i) => {
    const v = vehicles[i];
    if (!v) return;
    if (!window.confirm('Supprimer ce véhicule peut impacter vos devis et factures associés (le véhicule pourrait ne plus apparaître). Êtes-vous sûr de vouloir continuer ?')) return;
    setVehicles(list => list.filter((_, idx) => idx !== i));
    try { await pb.collection('vehicles').delete(v.id); } catch (e) { /* ignore */ }
  };

  const loadVehicleFor = async (record) => {
    const vid = record?.vehicle_id || record?.vehicle || null;
    if (!vid) return null;
    try { return await pb.collection('vehicles').getOne(vid); } catch { return null; }
  };

  const handleDownloadQuote = async (q) => {
    const vehicle = await loadVehicleFor(q);
    await generateQuotePDF(q, customer, vehicle, siteSettings || {}, 'download');
  };
  const handlePreviewQuote = async (q) => {
    const vehicle = await loadVehicleFor(q);
    await generateQuotePDF(q, customer, vehicle, siteSettings || {}, 'preview');
  };

  const handleShareQuote = async (q) => {
    try {
      const vehicle = await loadVehicleFor(q);
      const blob = await generateQuotePDF(q, customer, vehicle, siteSettings || {}, 'blob');
      if (!blob) {
        alert('Impossible de préparer le document à partager.');
        return;
      }
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], 'Devis.pdf', { type: 'application/pdf' })] })) {
        const file = new File([blob], `Devis_${q.quote_number || q.id}.pdf`, { type: 'application/pdf' });
        await navigator.share({
          title: 'Devis Christophe Dépanne',
          text: 'Voici votre devis.',
          files: [file]
        });
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('Le partage natif n\'est pas disponible sur cet appareil. Le devis a été ouvert dans un nouvel onglet.');
      }
    } catch (e) {
      console.error('Erreur partage devis:', e);
      alert('Erreur lors du partage du devis.');
    }
  };

  const handleDownloadInvoice = async (inv) => {
    const vehicle = await loadVehicleFor(inv);
    await generateInvoicePDF(inv, customer, vehicle, siteSettings || {}, 'download');
  };
  const handlePreviewInvoice = async (inv) => {
    const vehicle = await loadVehicleFor(inv);
    await generateInvoicePDF(inv, customer, vehicle, siteSettings || {}, 'preview');
  };

  const handleShareInvoice = async (inv) => {
    try {
      const vehicle = await loadVehicleFor(inv);
      const blob = await generateInvoicePDF(inv, customer, vehicle, siteSettings || {}, 'blob');
      if (!blob) {
        alert('Impossible de préparer le document à partager.');
        return;
      }
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], 'Facture.pdf', { type: 'application/pdf' })] })) {
        const file = new File([blob], `Facture_${inv.invoice_number || inv.id}.pdf`, { type: 'application/pdf' });
        await navigator.share({
          title: 'Facture Christophe Dépanne',
          text: 'Voici votre facture.',
          files: [file]
        });
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        alert('Le partage natif n\'est pas disponible sur cet appareil. La facture a été ouverte dans un nouvel onglet.');
      }
    } catch (e) {
      console.error('Erreur partage facture:', e);
      alert('Erreur lors du partage de la facture.');
    }
  };
  
  const openQuoteArchivesModal = async (quote) => {
    if (!quote?.id) return;
    const quoteId = quote.id;
    setArchivesModalOpen(true);
    setArchivesModalLoading(true);
    setArchivesModalTitle(`Archives du devis ${quote.quote_number || quote.number || quote.id}`);
    try {
      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "quotes" && record_id = "${quoteId}"`,
        sort: '-created'
      }).catch(() => []);
      setArchivesModalItems(archives || []);
    } catch (e) {
      console.error('Erreur chargement archives devis (popup compte):', e);
      setArchivesModalItems([]);
    } finally {
      setArchivesModalLoading(false);
    }
  };

  const openInvoiceArchivesModal = async (invoice) => {
    if (!invoice?.id) return;
    const invId = invoice.id;
    setArchivesModalOpen(true);
    setArchivesModalLoading(true);
    setArchivesModalTitle(`Archives de la facture ${invoice.invoice_number || invoice.number || invoice.id}`);
    try {
      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "invoices" && record_id = "${invId}"`,
        sort: '-created'
      }).catch(() => []);
      setArchivesModalItems(archives || []);
    } catch (e) {
      console.error('Erreur chargement archives factures (popup compte):', e);
    }
  };

  const openArchiveAtLogTime = async (log) => {
    const coll = log.collection;
    const recordId = log.record_id;
    if (!coll || !recordId) return;
    const logDate = new Date(log.created);
    try {
      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "${coll}" && record_id = "${recordId}"`,
        sort: '-created'
      }).catch(() => []);
      if (!archives || archives.length === 0) return;

      let chosen = null;
      for (const arch of archives) {
        const aDate = new Date(arch.created);
        if (aDate <= logDate) {
          chosen = arch;
          break;
        }
      }
      if (!chosen) {
        chosen = archives[archives.length - 1];
      }
      const fileName = chosen.pdf_file || chosen.file || '';
      if (!fileName) return;
      const url = `${pb.baseUrl}/api/files/document_archives/${chosen.id}/${fileName}`;
      window.open(url, '_blank');
    } catch (e) {
      console.error('Erreur ouverture archive pour notification (compte):', e);
    }
  };

  const updateQuoteStatus = async (quote, status) => {
    if (!quote?.id) return;
    const quoteId = quote.id;
    const oldStatus = quote.status || null;
    setQuoteSavingId(quoteId);
    try {
      // 1) Mise à jour du devis
      await pb.collection('quotes').update(quoteId, { status });
      setQuotes(list => list.map(q => (q.id === quoteId ? { ...q, status } : q)));

      // 2) Log dans activity_logs
      try {
        await pb.collection('activity_logs').create({
          collection: 'quotes',
          record_id: quoteId,
          action: 'status_change',
          performed_by_user: user?.id || null,
          performed_by_role: 'client',
          source: 'account',
          context: { from_status: oldStatus, to_status: status },
          message: `Client a changé le statut du devis en "${status}" depuis l'espace Mon Compte.`
        });
      } catch (logErr) {
        console.error('Erreur lors de la création du log activité devis:', logErr);
      }

      // 3) Archivage du PDF dans document_archives (avec version)
      try {
        const vehicle = await loadVehicleFor(quote);
        const settings = {};
        const blob = await generateQuotePDF({ ...quote, status }, customer, vehicle, settings, 'blob');
        if (blob) {
          // Calculer la prochaine version pour ce devis côté client
          let version = 1;
          try {
            const existing = await pb.collection('document_archives').getFullList({
              filter: `collection = "quotes" && record_id = "${quoteId}"`
            });
            version = (existing?.length || 0) + 1;
          } catch (e) {
            console.warn('Impossible de calculer la version du devis (compte) pour document_archives:', e);
          }

          const fd = new FormData();
          fd.append('collection', 'quotes');
          fd.append('record_id', quoteId);
          fd.append('select', 'quote');
          fd.append('generated_from', 'account');
          fd.append('version', String(version));
          if (user?.id) fd.append('generated_by', user.id);
          fd.append('snapshot_data', JSON.stringify({
            reason: 'status_change',
            status,
            oldStatus,
            quote_number: quote.quote_number || quote.number || quote.id,
            total: quote.total || null,
            customer_id: quote.customer_id || null,
            vehicle_id: quote.vehicle_id || null,
            version
          }));
          const fileName = `Devis_${quote.quote_number || quote.id}.pdf`;
          const file = new File([blob], fileName, { type: 'application/pdf' });
          fd.append('pdf_file', file);
          await pb.collection('document_archives').create(fd);
        }
      } catch (archErr) {
        console.error('Erreur lors de l\'archivage du PDF de devis:', archErr);
      }
    } catch (e) {
      console.error('Erreur maj statut devis:', e);
      alert('Impossible de mettre à jour le statut du devis.');
    } finally {
      setQuoteSavingId(null);
    }
  };

  const title = useMemo(() => 'Espace client', []);

  if (!isLoggedIn) {
    return (
      <div className="account-page">
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '85vh' }}>
          <div className="auth-gateway" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.08)', padding: 24, maxWidth: 980, width: '100%' }}>
            {/* Header centré */}
            <div className="auth-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', marginBottom: 16 }}>
              <img src="/images/logo.jpeg" alt="Christophe Dépanne" style={{ width: 84, height: 84, borderRadius: 16, objectFit: 'cover' }} />
              <h1 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>Espace client</h1>
              <div style={{ color: '#6b7280' }}>Retrouvez et gérez vos documents en ligne</div>
            </div>
            <div className="auth-grid">
              {/* Colonne gauche: Connexion */}
              <div className="auth-col-left">
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
                  <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: '1.25rem', color: '#111827' }}>Connexion</h2>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!loginEmail || !loginPassword) {
                        setLoginError('Veuillez renseigner votre email et votre mot de passe.');
                        return;
                      }
                      try {
                        setLoginLoading(true);
                        setLoginError('');
                        await pb.collection('users').authWithPassword(loginEmail, loginPassword);
                        // Rediriger vers /mon-compte une fois connecté
                        window.location.href = '/mon-compte';
                      } catch (err) {
                        setLoginError('Identifiants invalides. Vérifiez votre email et mot de passe.');
                      } finally {
                        setLoginLoading(false);
                      }
                    }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  >
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ color: '#374151', fontWeight: 600 }}>Email</span>
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="vous@exemple.com"
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                        required
                      />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ color: '#374151', fontWeight: 600 }}>Mot de passe</span>
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                        required
                      />
                    </label>
                    {loginError && (
                      <div style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fee2e2', padding: 10, borderRadius: 8 }}>{loginError}</div>
                    )}
                    <button type="submit" className="btn-primary" disabled={loginLoading} style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 700 }}>
                      {loginLoading ? 'Connexion…' : 'Se connecter'}
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <a href="/inscription" style={{ fontSize: 14, textDecoration: 'none', color: '#2563eb' }}>Créer un compte</a>
                      <a href="/reset-password" style={{ fontSize: 14, textDecoration: 'none', color: '#6b7280' }}>Mot de passe oublié ?</a>
                    </div>
                  </form>
                </div>
              </div>
              {/* Colonne droite: Avantages + CTA */}
              <div className="auth-col-right" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', color: '#374151' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#10b981" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-4-4 1.41-1.41L11 13.17l5.59-5.59L18 9l-7 8z"/></svg>
                    <span>Retrouvez votre dossier à tout moment</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#10b981" aria-hidden="true"><path d="M21 8V7l-3 2-2-1-2 1-2-1-2 1-2-1-3 1v9h18V8zM3 6l3-1 2 1 2-1 2 1 2-1 2 1 3-2v2l-3 2-2-1-2 1-2-1-2 1-2-1L3 8V6z"/></svg>
                    <span>Consultez vos devis et factures</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#10b981" aria-hidden="true"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M13,9V3.5L18.5,9H13M12,12L8,16H11V20H13V16H16L12,12Z"/></svg>
                    <span>Téléchargez vos documents en PDF</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#10b981" aria-hidden="true"><path d="M13 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3m-1 5v6l5.25 3.15.75-1.23-4.5-2.67V8H12z"/></svg>
                    <span>Suivez l’historique des actions</span>
                  </li>
                </ul>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, color: '#374151' }}>
                  Astuce: si vous n’avez pas encore de compte, cliquez sur « S’inscrire » pour créer votre accès en 1 minute.
                </div>
                <div className="auth-cta">
                  <a href="/inscription" className="btn-primary" style={{ padding: '10px 16px', borderRadius: 8, color: '#fff', textDecoration: 'none', fontWeight: 700, background: '#2563eb' }}>S’inscrire</a>
                  <a href="/" className="btn" style={{ padding: '10px 16px', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827', textDecoration: 'none', fontWeight: 600, background: '#fff' }}>Retour au site</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-topbar">
          <div className="brand">
            <img src="/images/logo.jpeg" alt="Christophe Dépanne" className="brand-logo" />
            <span className="brand-name">Christophe Dépanne</span>
          </div>
          <div className="head-right">
            <span className="head-title">Espace client</span>
            <div className="notif-icon-wrap">
              <button
                type="button"
                className="notif-icon-btn"
                onClick={() => setShowNotifMenu(v => !v)}
                aria-label="Notifications"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5l-1.7 1.7A1 1 0 0 0 5 19h14a1 1 0 0 0 .7-1.7L18 16z"/>
                </svg>
                {historyUnread > 0 && (
                  <span className="notif-badge-icon">{historyUnread}</span>
                )}
              </button>
              {showNotifMenu && (
                <div className="notif-dropdown">
                  <div className="notif-dropdown-header">Dernières notifications</div>
                  {history.length === 0 ? (
                    <div className="notif-empty">Aucune notification récente.</div>
                  ) : (
                    <ul className="notif-list">
                      {history.slice(0, 5).map(log => {
                        const coll = log.collection || '';
                        const typeLabel = coll === 'quotes' ? 'Devis' : coll === 'invoices' ? 'Facture' : (coll || 'Autre');
                        const isClientAction = log.source === 'account' || log.performed_by_role === 'client';

                        let actionLabel = '';
                        switch (log.action) {
                          case 'create':
                            actionLabel = 'Création';
                            break;
                          case 'update':
                            actionLabel = 'Mise à jour';
                            break;
                          case 'delete':
                            actionLabel = 'Suppression';
                            break;
                          case 'status_change':
                            actionLabel = 'Statut modifié';
                            break;
                          case 'pdf_generated':
                            actionLabel = 'PDF généré';
                            break;
                          case 'share':
                            actionLabel = 'Partage';
                            break;
                          default:
                            actionLabel = log.action || '';
                        }

                        let displayText = '';
                        if (coll === 'quotes' && log.action === 'status_change') {
                          const toStatus = log.context?.to_status || log.context?.toStatus || null;
                          if (isClientAction) {
                            if (toStatus === 'accepted') displayText = 'Vous avez accepté ce devis.';
                            else if (toStatus === 'rejected') displayText = 'Vous avez refusé ce devis.';
                            else if (toStatus === 'sent') displayText = 'Vous avez annulé votre réponse à ce devis.';
                            else displayText = 'Vous avez modifié le statut de ce devis.';
                          } else {
                            if (toStatus === 'accepted') displayText = 'Votre devis a été marqué comme accepté.';
                            else if (toStatus === 'rejected') displayText = 'Votre devis a été marqué comme refusé.';
                            else displayText = 'Le statut de votre devis a été mis à jour.';
                          }
                        } else if (coll === 'invoices' && log.action === 'create') {
                          displayText = isClientAction
                            ? 'Vous avez créé une nouvelle facture.'
                            : 'Vous avez reçu une nouvelle facture.';
                        } else if (coll === 'quotes' && log.action === 'create') {
                          displayText = isClientAction
                            ? 'Vous avez créé un nouveau devis.'
                            : 'Un nouveau devis a été créé pour vous.';
                        } else if (log.action === 'pdf_generated') {
                          if (coll === 'quotes') displayText = 'Un PDF de votre devis a été généré.';
                          else if (coll === 'invoices') displayText = 'Un PDF de votre facture a été généré.';
                        } else if (log.action === 'share') {
                          if (coll === 'quotes') displayText = isClientAction
                            ? 'Vous avez partagé votre devis.'
                            : 'Votre devis a été partagé.';
                          else if (coll === 'invoices') displayText = isClientAction
                            ? 'Vous avez partagé votre facture.'
                            : 'Votre facture a été partagée.';
                        }

                        if (!displayText) {
                          displayText = log.message || `Action ${actionLabel || ''} sur ${typeLabel}`;
                        }
                        if (displayText) {
                          displayText = displayText.replace(" depuis l'admin", '').replace(' depuis l’admin', '');
                        }

                        return (
                          <li key={log.id} className="notif-item">
                            <div className="notif-main">
                              <div className="notif-text">{displayText}</div>
                              <div className="notif-sub">{formatDateTime(log.created)}</div>
                            </div>
                            <span className="notif-type">{typeLabel}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <button
                    type="button"
                    className="notif-see-all"
                    onClick={() => {
                      setShowNotifMenu(false);
                      setTab('Historique');
                    }}
                  >
                    Voir l'historique
                  </button>
                </div>
              )}
            </div>
            <a className="back-link" href="/" aria-label="Retour au site">Retour au site</a>
          </div>
        </div>
        <div className="welcome-logo-wrap">
          <img src="/images/logo.jpeg" alt="Christophe Dépanne" className="welcome-logo" />
        </div>
        <div className="account-welcome">Bienvenue dans votre espace client !</div>
        <div className="account-header">
          <div className="subtitle">Gérez vos informations, vos véhicules et consultez vos documents.</div>
        </div>

        <div className="overview-card">
          <div
            className="overview-item overview-clickable"
            onClick={() => setTab('Devis')}
          >
            <div className="overview-label">Devis en attente de réponse</div>
            <div className="overview-value">{pendingQuotesCount}</div>
            <div className="overview-cta">
              <button
                type="button"
                className="overview-link-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setTab('Devis');
                }}
              >
                Voir mes devis
              </button>
            </div>
          </div>
          <div
            className="overview-item overview-clickable"
            onClick={() => setTab('Factures')}
          >
            <div className="overview-label">Factures impayées</div>
            <div className="overview-value">{unpaidInvoicesCount}</div>
            <div className="overview-cta">
              <button
                type="button"
                className="overview-link-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setTab('Factures');
                }}
              >
                Voir mes factures
              </button>
            </div>
          </div>
          <div
            className="overview-item wide overview-clickable"
            onClick={() => setTab('Historique')}
          >
            <div className="overview-label">Dernière activité</div>
            {lastActivity ? (
              <div className="overview-activity">
                <span className="activity-text">{lastActivityDisplayText || 'Dernière mise à jour de vos documents.'}</span>
                <span className="activity-date">{formatDateTime(lastActivity.created)}</span>
              </div>
            ) : (
              <div className="overview-empty">Aucune activité enregistrée pour le moment.</div>
            )}
            <div className="overview-cta">
              <button
                type="button"
                className="overview-link-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setTab('Historique');
                }}
              >
                Voir mon historique
              </button>
            </div>
          </div>
        </div>

        <div className="tabs">
          {tabs.map(t => (
            <button
              key={t}
              className={`tab ${tab===t?'active':''}`}
              onClick={()=>setTab(t)}
            >
              {t}
              {t === 'Devis' && pendingQuotesCount > 0 && (
                <span className="notif-badge">{pendingQuotesCount}</span>
              )}
              {t === 'Factures' && unpaidInvoicesCount > 0 && (
                <span className="notif-badge">{unpaidInvoicesCount}</span>
              )}
              {t === 'Rendez-vous' && proposedSlotsCount > 0 && (
                <span className="notif-badge">{proposedSlotsCount}</span>
              )}
              {t === 'Historique' && historyUnread > 0 && (
                <span className="notif-badge">{historyUnread}</span>
              )}
            </button>
          ))}
        </div>

        {loading && <div className="card">Chargement…</div>}

        {!loading && tab === 'Accueil' && (
          <div className="card">
            <h2>Bienvenue dans votre espace</h2>
            <div className="home-grid">
              <div className="home-card">
                <div className="home-card-title">Mes informations</div>
                <div className="home-card-meta">Consulter ou mettre à jour vos coordonnées client.</div>
                <div className="home-card-actions">
                  <button className="btn" onClick={() => setTab('Profil')}>Accéder au profil</button>
                </div>
              </div>
              <div className="home-card">
                <div className="home-card-title">Mes véhicules</div>
                <div className="home-card-meta">{vehicles.length === 0 ? 'Aucun véhicule enregistré.' : `${vehicles.length} véhicule(s) enregistré(s).`}</div>
                <div className="home-card-actions">
                  <button className="btn" onClick={() => setTab('Véhicules')}>Gérer mes véhicules</button>
                </div>
              </div>
              <div className="home-card">
                <div className="home-card-title">Mes devis</div>
                <div className="home-card-meta">
                  {quotes.length === 0
                    ? 'Aucun devis pour le moment.'
                    : `${quotes.length} devis dont ${pendingQuotesCount} en attente de réponse.`}
                </div>
                <div className="home-card-actions">
                  <button className="btn" onClick={() => setTab('Devis')}>Voir mes devis</button>
                </div>
              </div>
              <div className="home-card">
                <div className="home-card-title">Mes factures</div>
                <div className="home-card-meta">
                  {invoices.length === 0
                    ? 'Aucune facture disponible.'
                    : `${invoices.length} facture(s), ${unpaidInvoicesCount} impayée(s).`}
                </div>
                <div className="home-card-actions">
                  <button className="btn" onClick={() => setTab('Factures')}>Voir mes factures</button>
                </div>
              </div>
              <div className="home-card wide">
                <div className="home-card-title">Historique et notifications</div>
                <div className="home-card-meta">Consultez toutes les dernières actions sur vos devis et factures.</div>
                <div className="home-card-actions">
                  <button className="btn" onClick={() => setTab('Historique')}>Voir l'historique</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && tab === 'Rendez-vous' && (
          <div className="card">
            <h2>Vos rendez-vous</h2>
            <div className="view-switch" style={{ display:'flex', gap:8, margin:'8px 0 16px 0', flexWrap:'wrap' }}>
              <button className={`btn ${appointmentsView==='list'?'btn-primary':'btn'}`} onClick={()=>setAppointmentsView('list')}>Liste</button>
              <button className={`btn ${appointmentsView==='calendar'?'btn-primary':'btn'}`} onClick={()=>setAppointmentsView('calendar')}>Calendrier</button>
            </div>
            {appointments.filter(a => a.visible_client !== false).length === 0 ? (
              <div className="empty">Aucun rendez-vous pour le moment.</div>
            ) : appointmentsView === 'calendar' ? (
              <div className="appointments-calendar" style={{ background:'#fff' }}>
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
                  locales={[frLocale]}
                  locale="fr"
                  height="auto"
                  events={appointments.filter(a => a.visible_client !== false).map(a => ({
                    id: a.id,
                    title: a.title || 'Rendez-vous',
                    start: a.start_date,
                    end: a.end_date || a.start_date,
                    backgroundColor: getApptStatusColor(a.status),
                    borderColor: getApptStatusColor(a.status),
                  }))}
                />
              </div>
            ) : (
              <div className="list">
                {(() => {
                  const now = new Date();
                  const visibleAppts = appointments.filter(a => a.visible_client !== false);
                  const upcoming = visibleAppts.filter(a => new Date(a.start_date || 0) >= now);
                  const past = visibleAppts.filter(a => new Date(a.start_date || 0) < now);
                  const renderAppt = (appt) => {
                    const slots = (appointmentSlots[appt.id] || []).sort((a,b) => new Date(a.start) - new Date(b.start));
                    const hasProposed = slots.length > 0;
                    const isPast = appt.start_date ? (new Date(appt.start_date) < new Date()) : false;
                    return (
                      <div key={appt.id} className="list-item" style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:12, marginBottom:12 }}>
                        <div className="row between" style={{ gap:12, alignItems:'center' }}>
                          <div>
                            <div style={{ fontWeight:700 }}>{appt.title || 'Rendez-vous'}</div>
                            <div style={{ color:'#6b7280', fontSize:14 }}>
                              {!appt.start_date
                                ? 'En attente de confirmation'
                                : `${isPast ? 'Effectué' : 'Prévu'} le ${new Date(appt.start_date).toLocaleString('fr-FR', { dateStyle:'short', timeStyle:'short' })}`}
                              {appt.start_date && appt.end_date && (
                                <span style={{ marginLeft:8, color:'#9ca3af' }}>• Durée {formatDuration(appt.start_date, appt.end_date)}</span>
                              )}
                            </div>
                            {appt.expand?.vehicle_id && (
                              <div style={{ color:'#6b7280', fontSize:13 }}>
                                Véhicule: {appt.expand.vehicle_id.brand} {appt.expand.vehicle_id.model}{appt.expand.vehicle_id.license_plate ? ` (${appt.expand.vehicle_id.license_plate})` : ''}
                              </div>
                            )}
                          </div>
                          {hasProposed && (
                            <span className="badge" style={{ background:'#2563eb', color:'#fff', borderRadius:9999, padding:'2px 8px', fontSize:12 }}>{slots.length} créneau{slots.length>1?'x':''} proposés</span>
                          )}
                        </div>
                        {(appt.start_date && !hasProposed && !isPast) && (
                          <div style={{ marginTop:10, display:'flex', justifyContent:'flex-end' }}>
                            <button className="btn" onClick={() => handleAddToCalendar(appt)}>Ajouter à mon agenda (.ics)</button>
                          </div>
                        )}
                        {hasProposed && (
                          <div style={{ marginTop:10, display:'grid', gap:8 }}>
                            {slots.map(s => (
                              <div key={s.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1px solid #f3f4f6', padding:8, borderRadius:8 }}>
                                <div>
                                  <strong>{new Date(s.start).toLocaleDateString('fr-FR')}</strong>
                                  <span style={{ marginLeft:8 }}>{new Date(s.start).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})} → {new Date(s.end).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>
                                </div>
                                <button
                                  className="btn-primary"
                                  disabled={appointmentSavingId === appt.id}
                                  onClick={() => acceptAppointmentSlot(appt, s)}
                                >
                                  Accepter ce créneau
                                </button>
                              </div>
                            ))}
                            <div style={{ display:'flex', justifyContent:'flex-end' }}>
                              <button className="btn" disabled={appointmentSavingId === appt.id} onClick={() => refuseAllAppointmentSlots(appt)}>Refuser tous les créneaux</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  };
                  return (
                    <>
                      <h3 style={{ margin:'12px 0 6px 0' }}>À venir</h3>
                      {upcoming.length === 0 ? <div className="empty">Aucun rendez-vous à venir</div> : upcoming.map(renderAppt)}
                      <h3 style={{ margin:'18px 0 6px 0' }}>Passés</h3>
                      {past.length === 0 ? <div className="empty">Aucun rendez-vous passé</div> : past.map(renderAppt)}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'Profil' && (
          <div className="card">
            <h2>Profil</h2>
            {!customer ? (
              <div className="empty">Aucun profil client n'est encore associé à votre compte.</div>
            ) : (
              <>
                <div className="profile-summary">
                  <div className="profile-main-name">{customer.name || 'Profil client'}</div>
                  <div className="profile-main-meta">
                    {customer.email && <span>{customer.email}</span>}
                    {customer.phone && <span>{customer.phone}</span>}
                    {customer.address && <span>{customer.address}</span>}
                  </div>
                </div>
                <div className="grid profile-grid">
                  <input placeholder="Nom" value={customer.name||''} onChange={e=>setCustomer({ ...customer, name:e.target.value })} readOnly={!editingProfile} />
                  <input placeholder="Email" value={customer.email||''} onChange={e=>setCustomer({ ...customer, email:e.target.value })} readOnly={!editingProfile} />
                  <input placeholder="Téléphone" value={customer.phone||''} onChange={e=>setCustomer({ ...customer, phone:e.target.value })} readOnly={!editingProfile} />
                  <input placeholder="Adresse" value={customer.address||''} onChange={e=>setCustomer({ ...customer, address:e.target.value })} readOnly={!editingProfile} />
                  <input placeholder="Société (si pro)" value={customer.company||''} onChange={e=>setCustomer({ ...customer, company:e.target.value })} readOnly={!editingProfile} />
                </div>
              </>
            )}
            <div className="actions">
              {!editingProfile && customer && (
                <button className="btn" onClick={startEditProfile}>Modifier mes informations</button>
              )}
              {editingProfile && (
                <>
                  <button className="btn primary" disabled={!customer || saving} onClick={async () => { await handleProfileSave(); setEditingProfile(false); }}>Enregistrer</button>
                  <button className="btn" onClick={cancelEditProfile}>Annuler</button>
                </>
              )}
            </div>
          </div>
        )}

        {!loading && tab === 'Véhicules' && (
          <div className="card">
            <div className="row between">
              <h2>Véhicules</h2>
              <button className="btn" onClick={handleAddVehicle}>Ajouter un véhicule</button>
            </div>
            {vehicles.length === 0 ? (
              <div className="empty">Aucun véhicule actuellement.</div>
            ) : (
              vehicles.map((v, i) => (
                <div key={v.id||i} className="vehicle-block">
                  <button
                    type="button"
                    className="vehicle-delete"
                    aria-label="Supprimer ce véhicule"
                    onClick={()=>handleDeleteVehicle(i)}
                  >
                    ×
                  </button>
                  {(() => {
                    const vid = v.id || v._tempId || `idx-${i}`;
                    const isEditing = editingVehicleId === vid;
                    const titleParts = [];
                    if (v.brand) titleParts.push(v.brand);
                    if (v.model) titleParts.push(v.model);
                    const vehicleTitle = titleParts.length > 0 ? titleParts.join(' ') : 'Véhicule';
                    return (
                      <>
                        <div className="vehicle-header">
                          <div className="vehicle-header-main">
                            <div className="vehicle-title">{vehicleTitle}</div>
                            {v.license_plate && (
                              <div className="vehicle-sub">Immatriculation : {v.license_plate}</div>
                            )}
                          </div>
                          <div className="vehicle-tag">Véhicule n°{i+1}</div>
                        </div>
                        <div className="grid">
                          <input placeholder="Marque" value={v.brand||''} onChange={e=>handleUpdateVehicle(i,{ brand:e.target.value })} readOnly={!isEditing} />
                          <input placeholder="Modèle" value={v.model||''} onChange={e=>handleUpdateVehicle(i,{ model:e.target.value })} readOnly={!isEditing} />
                          <input placeholder="Année" value={v.year||''} onChange={e=>handleUpdateVehicle(i,{ year:e.target.value })} readOnly={!isEditing} />
                          <input placeholder="Immatriculation" value={v.license_plate||''} onChange={e=>handleUpdateVehicle(i,{ license_plate:e.target.value })} readOnly={!isEditing} />
                          <input placeholder="Kilométrage" value={v.mileage||''} onChange={e=>handleUpdateVehicle(i,{ mileage:e.target.value })} readOnly={!isEditing} />
                        </div>
                        <div className="actions">
                          {!isEditing && (
                            <button className="btn" onClick={()=>startEditVehicle(i)}>Modifier</button>
                          )}
                          {isEditing && (
                            <>
                              <button className="btn primary" disabled={savingVehicleId===vid} onClick={()=>saveVehicle(i)}>
                                {savingVehicleId===vid ? 'Enregistrement…' : 'Enregistrer'}
                              </button>
                              <button className="btn" onClick={()=>cancelEditVehicle(i)}>Annuler</button>
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        )}

        {!loading && tab === 'Contact' && (
          <div className="card">
            <h2>Contact & mentions</h2>
            <div className="contact-block">
              <div className="contact-section">
                <h3>Coordonnées</h3>
                <div className="contact-line"><span className="clabel">Nom</span><span className="cvalue">EURL Soucasse</span></div>
                <div className="contact-line"><span className="clabel">Email</span><a href="mailto:soucasse1976@gmail.com" className="cvalue">soucasse1976@gmail.com</a></div>
                <div className="contact-line"><span className="clabel">Téléphone</span><a href="tel:0645219585" className="cvalue">06 45 21 95 85</a></div>
                <div className="contact-line"><span className="clabel">Adresse</span><span className="cvalue">190 allée de lahage, 31370 Lahage</span></div>
              </div>
              <div className="contact-section">
                <h3>Informations légales</h3>
                <div className="contact-line"><span className="clabel">SIRET</span><span className="cvalue">83823795600027</span></div>
                <div className="contact-line"><span className="clabel">SIREN</span><span className="cvalue">838237956</span></div>
                <div className="contact-line"><span className="clabel">TVA</span><span className="cvalue">FR35838237956</span></div>
                <div className="contact-line"><span className="clabel">RCS</span><span className="cvalue">838 237 956 R.C.S. Toulouse</span></div>
              </div>
              <div className="contact-help">
                Pour toute question sur vos devis, vos factures ou une intervention en cours, vous pouvez nous contacter par téléphone ou par email. Nous vous répondrons dans les plus brefs délais.
              </div>
            </div>
          </div>
        )}

        {!loading && tab === 'Historique' && (
          <div className="card">
            <h2>Historique de vos documents</h2>
            {history.length === 0 ? (
              <div className="empty">Aucune notification pour le moment.</div>
            ) : (
              <div className="history-list">
                {history.map(log => {
                  const coll = log.collection || '';
                  const typeLabel = coll === 'quotes' ? 'Devis' : coll === 'invoices' ? 'Facture' : (coll || 'Autre');
                  const relatedQuote = coll === 'quotes' ? quotes.find(q => q.id === log.record_id) : null;
                  const relatedInvoice = coll === 'invoices' ? invoices.find(inv => inv.id === log.record_id) : null;
                  const isClientAction = log.source === 'account' || log.performed_by_role === 'client';

                  let actionLabel = '';
                  switch (log.action) {
                    case 'create':
                      actionLabel = 'Création';
                      break;
                    case 'update':
                      actionLabel = 'Mise à jour';
                      break;
                    case 'delete':
                      actionLabel = 'Suppression';
                      break;
                    case 'status_change':
                      actionLabel = 'Statut modifié';
                      break;
                    case 'pdf_generated':
                      actionLabel = 'PDF généré';
                      break;
                    case 'share':
                      actionLabel = 'Partage';
                      break;
                    default:
                      actionLabel = log.action || '';
                  }

                  // Texte lisible pour le client
                  let displayText = '';
                  if (coll === 'quotes' && log.action === 'status_change') {
                    const toStatus = log.context?.to_status || log.context?.toStatus || null;
                    if (isClientAction) {
                      if (toStatus === 'accepted') displayText = 'Vous avez accepté ce devis.';
                      else if (toStatus === 'rejected') displayText = 'Vous avez refusé ce devis.';
                      else if (toStatus === 'sent') displayText = 'Vous avez annulé votre réponse à ce devis.';
                      else displayText = 'Vous avez modifié le statut de ce devis.';
                    } else {
                      if (toStatus === 'accepted') displayText = 'Votre devis a été marqué comme accepté.';
                      else if (toStatus === 'rejected') displayText = 'Votre devis a été marqué comme refusé.';
                      else if (toStatus === 'sent') displayText = 'Le devis a été modifié et est de nouveau en attente de votre réponse.';
                      else displayText = 'Devis en attente de réponse.';
                    }
                  } else if (coll === 'invoices' && log.action === 'create') {
                    displayText = isClientAction
                      ? 'Vous avez créé une nouvelle facture.'
                      : 'Vous avez reçu une nouvelle facture.';
                  } else if (coll === 'quotes' && log.action === 'create') {
                    displayText = isClientAction
                      ? 'Vous avez créé un nouveau devis.'
                      : 'Un nouveau devis a été créé pour vous.';
                  } else if (log.action === 'pdf_generated') {
                    if (coll === 'quotes') displayText = 'Un PDF de votre devis a été généré.';
                    else if (coll === 'invoices') displayText = 'Un PDF de votre facture a été généré.';
                  } else if (log.action === 'share') {
                    if (coll === 'quotes') displayText = isClientAction
                      ? 'Vous avez partagé votre devis.'
                      : 'Votre devis a été partagé.';
                    else if (coll === 'invoices') displayText = isClientAction
                      ? 'Vous avez partagé votre facture.'
                      : 'Votre facture a été partagée.';
                  }

                  if (!displayText) {
                    displayText = log.message || `Action ${actionLabel || ''} sur ${typeLabel}`;
                  }
                  // Nettoyer les mentions internes "depuis l'admin" pour l'affichage client
                  if (displayText) {
                    displayText = displayText.replace(" depuis l'admin", '').replace(' depuis l’admin', '');
                  }
                  return (
                    <div key={log.id} className="history-row">
                      <div className="history-main">
                        <div className="history-date">{formatDateTime(log.created)}</div>
                        <div className="history-text">
                          {displayText}
                        </div>
                      </div>
                      <div className="history-meta">
                        <span className="tag secondary">{typeLabel}</span>
                        {actionLabel && <span className="tag">{actionLabel}</span>}
                        {relatedQuote && (
                          <>
                            <button
                              type="button"
                              className="history-link-btn icon-only"
                              onClick={() => handleDownloadQuote(relatedQuote)}
                              aria-label="Télécharger le devis"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M5 20h14v-2H5v2zm7-16l-6 6h4v4h4v-4h4l-6-6z"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="history-link-btn"
                              onClick={() => handlePreviewQuote(relatedQuote)}
                            >
                              Voir le devis
                            </button>
                            <button
                              type="button"
                              className="history-link-btn"
                              onClick={() => openArchiveAtLogTime(log)}
                            >
                              Voir la version archivée
                            </button>
                          </>
                        )}
                        {relatedInvoice && (
                          <>
                            <button
                              type="button"
                              className="history-link-btn icon-only"
                              onClick={() => handleDownloadInvoice(relatedInvoice)}
                              aria-label="Télécharger la facture"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M5 20h14v-2H5v2zm7-16l-6 6h4v4h4v-4h4l-6-6z"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="history-link-btn"
                              onClick={() => handlePreviewInvoice(relatedInvoice)}
                            >
                              Voir la facture
                            </button>
                            <button
                              type="button"
                              className="history-link-btn"
                              onClick={() => openArchiveAtLogTime(log)}
                            >
                              Voir la version archivée
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'Devis' && (
          <div className="card">
            <h2>Vos devis</h2>
            <div className="list-tools">
              <input className="search" placeholder="Rechercher (numéro, statut)…" value={qSearch} onChange={e=>setQSearch(e.target.value)} />
            </div>
            {quotes.length === 0 ? (
              <div className="empty">Aucun devis pour le moment.</div>
            ) : (
              <div className="list">
                {quotes.filter(q => {
                  const text = `${q.quote_number||q.number||q.id} ${q.status||''}`.toLowerCase();
                  return text.includes(qSearch.toLowerCase());
                }).map(q => (
                  <React.Fragment key={q.id}>
                    <div className="list-row">
                      <div className="list-main">
                        <div className="title">Devis {q.quote_number || q.number || q.id}</div>
                        <div className="meta">
                          {new Date(q.created).toLocaleDateString()}
                          {q.total ? (
                            <>
                              {' '}
                              <span className="status-sep">•</span>
                              {' '}
                              <span>{formatAmount(q.total)}</span>
                            </>
                          ) : null}
                          {' '}
                          <span className="status-sep">•</span>
                          {' '}
                          <span
                            className={
                              `status-badge ${
                                q.status === 'accepted'
                                  ? 'accepted'
                                  : q.status === 'rejected'
                                  ? 'rejected'
                                  : q.status === 'sent'
                                  ? 'pending'
                                  : q.status === 'expired'
                                  ? 'expired'
                                  : 'default'
                              }`
                            }
                          >
                            {(q.status === 'accepted' && 'Accepté') ||
                              (q.status === 'rejected' && 'Refusé') ||
                              (q.status === 'sent' && 'En attente de réponse') ||
                              (q.status === 'expired' && 'Expiré') ||
                              q.status ||
                              'En cours'}
                          </span>
                        </div>
                      </div>
                      <div className="list-actions">
                        <button className="btn" disabled={isGenerating} onClick={()=>handlePreviewQuote(q)}>{isGenerating ? 'Ouverture…' : 'Aperçu'}</button>
                        <button className="btn" disabled={isGenerating} onClick={()=>handleDownloadQuote(q)}>{isGenerating ? 'Génération…' : 'Télécharger'}</button>
                        <button className="btn" disabled={isGenerating} onClick={()=>handleShareQuote(q)}>{isGenerating ? 'Préparation…' : 'Partager'}</button>
                        {q.status === 'sent' && (
                          <>
                            <button
                              className="btn primary"
                              disabled={!!quoteSavingId}
                              onClick={()=>updateQuoteStatus(q, 'accepted')}
                            >
                              {quoteSavingId === q.id ? 'Validation…' : 'Accepter'}
                            </button>
                            <button
                              className="btn"
                              disabled={!!quoteSavingId}
                              onClick={()=>updateQuoteStatus(q, 'rejected')}
                            >
                              {quoteSavingId === q.id ? 'Mise à jour…' : 'Refuser'}
                            </button>
                          </>
                        )}
                        {(q.status === 'accepted' || q.status === 'rejected') && (
                          <button
                            className="btn"
                            disabled={!!quoteSavingId}
                            onClick={()=>updateQuoteStatus(q, 'sent')}
                          >
                            {quoteSavingId === q.id ? 'Annulation…' : 'Annuler ma réponse'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="list-row archives-toggle-row">
                      <div className="list-main">
                        <button
                          type="button"
                          className="history-link-btn"
                          onClick={() => openQuoteArchivesModal(q)}
                        >
                          Voir les archives PDF
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'Factures' && (
          <div className="card">
            <h2>Vos factures</h2>
            <div className="list-tools">
              <input className="search" placeholder="Rechercher (numéro, statut)…" value={iSearch} onChange={e=>setISearch(e.target.value)} />
            </div>
            {invoices.length === 0 ? (
              <div className="empty">Aucune facture pour le moment.</div>
            ) : (
              <div className="list">
                {invoices.filter(inv => {
                  const text = `${inv.invoice_number||inv.number||inv.id} ${inv.status||''}`.toLowerCase();
                  return text.includes(iSearch.toLowerCase());
                }).map(inv => {
                  let statusClass = 'default';
                  let statusLabel = 'Émise';

                  if (inv.status === 'paid') {
                    statusClass = 'accepted';
                    statusLabel = 'Payée';
                  } else if (inv.status === 'unpaid' || inv.status === 'due' || inv.status === 'sent') {
                    statusClass = 'pending';
                    statusLabel = 'En attente de paiement';
                  } else if (inv.status === 'overdue') {
                    statusClass = 'expired';
                    statusLabel = 'En retard';
                  } else if (inv.status === 'cancelled' || inv.status === 'canceled') {
                    statusClass = 'rejected';
                    statusLabel = 'Annulée';
                  }

                  return (
                    <React.Fragment key={inv.id}>
                      <div className="list-row">
                        <div className="list-main">
                          <div className="title">Facture {inv.invoice_number || inv.number || inv.id}</div>
                          <div className="meta">
                            {new Date(inv.created).toLocaleDateString()}
                            {inv.total ? (
                              <>
                                {' '}
                                <span className="status-sep">•</span>
                                {' '}
                                <span>{formatAmount(inv.total)}</span>
                              </>
                            ) : null}
                            {' '}
                            <span className="status-sep">•</span>
                            {' '}
                            <span
                              className={`status-badge ${statusClass}`}
                            >
                              {statusLabel}
                            </span>
                          </div>
                        </div>
                        <div className="list-actions">
                          <button className="btn" disabled={isGenerating} onClick={()=>handlePreviewInvoice(inv)}>{isGenerating ? 'Ouverture…' : 'Aperçu'}</button>
                          <button className="btn" disabled={isGenerating} onClick={()=>handleDownloadInvoice(inv)}>{isGenerating ? 'Génération…' : 'Télécharger'}</button>
                          <button className="btn" disabled={isGenerating} onClick={()=>handleShareInvoice(inv)}>{isGenerating ? 'Préparation…' : 'Partager'}</button>
                        </div>
                      </div>
                      <div className="list-row archives-toggle-row">
                        <div className="list-main">
                          <button
                            type="button"
                            className="history-link-btn"
                            onClick={() => openInvoiceArchivesModal(inv)}
                          >
                            Voir les archives PDF
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  );})}
              </div>
            )}
          </div>
        )}
      </div>

      {archivesModalOpen && (
        <div className="modal-overlay" onClick={() => setArchivesModalOpen(false)}>
          <div className="archives-modal" onClick={(e) => e.stopPropagation()}>
            <div className="archives-modal-header">
              <h3>{archivesModalTitle}</h3>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={() => setArchivesModalOpen(false)}
                aria-label="Fermer les archives"
              >
                ×
              </button>
            </div>
            <div className="archives-modal-body">
              {archivesModalLoading ? (
                <div className="loading-spinner">Chargement des archives…</div>
              ) : archivesModalItems.length === 0 ? (
                <div className="empty">Aucune archive PDF enregistrée pour ce document.</div>
              ) : (
                <div className="history-list">
                  {archivesModalItems.map(arch => {
                    const fileName = arch.pdf_file || arch.file || '';
                    const url = fileName
                      ? `${pb.baseUrl}/api/files/document_archives/${arch.id}/${fileName}`
                      : null;
                    return (
                      <div key={arch.id} className="history-row">
                        <div className="history-main">
                          <div className="history-date">Version {arch.version || ''}</div>
                          <div className="history-text">{formatDateTime(arch.created)}</div>
                        </div>
                        <div className="history-meta">
                          {url && (
                            <>
                              <a
                                href={url}
                                download
                                className="history-link-btn icon-only"
                                aria-label="Télécharger la version PDF"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                  <path d="M5 20h14v-2H5v2zm7-16l-6 6h4v4h4v-4h4l-6-6z"/>
                                </svg>
                              </a>
                              <a href={url} target="_blank" rel="noreferrer" className="history-link-btn">
                                Ouvrir
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="account-footer">
        <div className="container">
          <div className="footer-inner">© {new Date().getFullYear()} Christophe Dépanne — Espace client</div>
        </div>
      </div>
    </div>
  );
}
